import { useState } from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  Building2,
  Check,
  ClipboardList,
  Copy,
  Fingerprint,
  KeyRound,
  Save,
  ShieldCheck,
} from 'lucide-react';
import type { Credenciales } from './tipos';

export function DialogoDeCredenciales({
  credenciales,
  alCerrar,
}: {
  credenciales: Credenciales;
  alCerrar: () => void;
}) {
  const lista = credencialesDe(credenciales);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-armazon-900/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="credenciales-titulo"
        className="flex max-h-[92dvh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="flex items-start gap-4 border-b border-armazon-100 p-5">
          <span className="grid size-12 shrink-0 place-items-center rounded-xl bg-marca-50 text-marca-600">
            <KeyRound className="size-6" aria-hidden />
          </span>
          <div className="min-w-0">
            <h2
              id="credenciales-titulo"
              className="text-base font-bold uppercase tracking-tight text-armazon-900"
            >
              Credenciales de {credenciales.clave}
            </h2>
            <p className="mt-0.5 text-xs font-semibold uppercase tracking-wide text-armazon-500">
              Entrégueselas al integrador ahora: no se pueden volver a ver.
            </p>
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-5">
          <p className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3.5 text-xs font-semibold uppercase leading-relaxed tracking-wide text-amber-900">
            <AlertTriangle className="mt-px size-4 shrink-0" aria-hidden />
            Esta es la única vez que aparecen. Si se pierden hay que regenerarlas, y las de
            ahora dejarán de funcionar en ese momento.
          </p>

          {lista.map((c) => (
            <Credencial key={c.tecnico} {...c} />
          ))}
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-armazon-100 bg-armazon-50/60 px-5 py-4">
          <CopiarTodo lista={lista} clave={credenciales.clave} />
          <button
            type="button"
            onClick={alCerrar}
            className="inline-flex h-11 items-center gap-2 rounded-xl bg-marca-600 px-5 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-marca-700"
          >
            <Save className="size-4" aria-hidden />
            Ya las guardé
          </button>
        </footer>
      </div>
    </div>
  );
}

interface Fila {
  icono: LucideIcon;
  rotulo: string;
  tecnico: string;
  paraQue: string;
  valor: string;
  secreto?: boolean;
}

function credencialesDe(credenciales: Credenciales): Fila[] {
  const filas: Fila[] = [];

  if (credenciales.clienteId) {
    filas.push({
      icono: Fingerprint,
      rotulo: 'Identificador de la institución',
      tecnico: 'client_id',
      paraQue: 'Va en la petición del token. No es secreto.',
      valor: credenciales.clienteId,
    });
  }
  if (credenciales.secreto) {
    filas.push({
      icono: KeyRound,
      rotulo: 'Clave para pedir el token',
      tecnico: 'client_secret',
      paraQue: 'Con esto se pide el token de acceso. No se comparte con nadie más.',
      valor: credenciales.secreto,
      secreto: true,
    });
  }
  if (credenciales.llave) {
    filas.push({
      icono: KeyRound,
      rotulo: 'Llave fija',
      tecnico: 'token',
      paraQue: 'Va en cada petición. Sólo para lo que ya estaba integrado contra la v1.',
      valor: credenciales.llave,
      secreto: true,
    });
  }
  if (credenciales.secretoDeFirma) {
    filas.push({
      icono: ShieldCheck,
      rotulo: 'Secreto para firmar cada petición',
      tecnico: 'hmac_secret',
      paraQue: 'Sin la firma, la petición se rechaza aunque el token sea válido.',
      valor: credenciales.secretoDeFirma,
      secreto: true,
    });
  }
  filas.push({
    icono: Building2,
    rotulo: 'Clave de la institución',
    tecnico: 'key',
    paraQue: 'La que va dentro del cuerpo, en el contrato de cooperativas.',
    valor: credenciales.clave,
  });

  return filas;
}

function CopiarTodo({ lista, clave }: { lista: Fila[]; clave: string }) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    const bloque = [
      `CREDENCIALES DE ${clave.toUpperCase()}`,
      '',
      ...lista.map((c) => `${c.tecnico}: ${c.valor}`),
    ].join('\n');

    if (await alPortapapeles(bloque)) {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }
  }

  return (
    <button
      type="button"
      onClick={() => void copiar()}
      className="inline-flex h-11 items-center gap-2 rounded-xl border border-armazon-200 bg-white px-4 text-xs font-bold uppercase tracking-wide text-armazon-700 transition hover:bg-armazon-100"
    >
      {copiado ? (
        <Check className="size-4 text-marca-600" aria-hidden />
      ) : (
        <ClipboardList className="size-4" aria-hidden />
      )}
      {copiado ? 'Copiadas' : 'Copiar todas'}
    </button>
  );
}

function Credencial({ icono: Icono, rotulo, tecnico, paraQue, valor, secreto = false }: Fila) {
  const [copiado, setCopiado] = useState(false);

  async function copiar() {
    if (await alPortapapeles(valor)) {
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    }
  }

  return (
    <div
      className={`rounded-xl border p-3.5 ${
        secreto ? 'border-amber-200 bg-amber-50/40' : 'border-armazon-200 bg-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <span
          className={`mt-0.5 grid size-8 shrink-0 place-items-center rounded-lg ${
            secreto ? 'bg-amber-100 text-amber-700' : 'bg-armazon-100 text-armazon-600'
          }`}
        >
          <Icono className="size-4" aria-hidden />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-xs font-bold uppercase tracking-wide text-armazon-800">
              {rotulo}
            </span>
            <code className="rounded-md bg-armazon-100 px-1.5 py-0.5 font-mono text-[10px] text-armazon-600">
              {tecnico}
            </code>
          </div>
          <p className="mt-0.5 text-[11px] leading-relaxed text-armazon-500">{paraQue}</p>
        </div>

        <button
          type="button"
          onClick={() => void copiar()}
          aria-label={`Copiar ${tecnico}`}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-armazon-500 transition hover:bg-armazon-100 hover:text-armazon-700"
        >
          {copiado ? (
            <Check className="size-3.5 text-marca-600" aria-hidden />
          ) : (
            <Copy className="size-3.5" aria-hidden />
          )}
          {copiado ? 'Copiado' : 'Copiar'}
        </button>
      </div>

      <code className="mt-2.5 block break-all rounded-lg bg-white/70 px-3 py-2 font-mono text-xs text-armazon-800 ring-1 ring-armazon-200/70">
        {valor}
      </code>
    </div>
  );
}

async function alPortapapeles(texto: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    return false;
  }
}
